import { useEffect, useRef, useState } from "react";
import type { OrbState } from "../orb";
import { useServerVoice, type TtsVoice } from "../voice";
import { deriveWebVoiceOwnerUxPolish, prepareOwnerTtsText } from "../voice/webVoiceOwnerUxPolish";
import { describeVoiceInputState } from "../voice/voiceTrace";
import {
  parseWakewordBuildGate,
  parseWakewordRequest,
  resolveWakewordGate,
  serializeWakewordRequest,
  WAKEWORD_REQUEST_KEY,
} from "../voice/wakewordGate";
import { api, hasSessionToken, type ApprovalInboxDecisionView, type AssistantDebugView, type ApprovalView, type Role as AgentRole } from "../api";
import { ownerFacingGlobalError } from "../ownerFacingGlobalError";
import { allApprovalsDecided, mergeDecision, type ApprovalDecision } from "./approvalDecisions";
import { detectSelfKnowledgePayload, type SelfKnowledgePayload } from "../selfKnowledgePayloads";
import { MODEL_KEY, runModelDefaultMigration } from "./modelMigration";
import { needsConvoSwitch } from "./cross-convo-resume";

export type ViewMode = "orb" | "compact";
export type MessageRole = "user" | "assistant" | "system" | "tool";

export interface UiMessage {
  id: string;
  role: MessageRole;
  content: string;
  meta?: {
    iterations: number;
    toolCalls: number;
    model: string | null;
    assistantMode?: string | null;
    sanityStatus?: string | null;
    activeToolGroups?: string[];
    usedToolNames?: string[];
    blockedToolGroups?: string[];
    latencies?: AssistantDebugView["latencies"] | null;
  };
  pending?: boolean;
  draft?: { webLink: string; tool: string };
  // SIMO-SELF-KNOWLEDGE-UI-1 Slice C: strukturierte Self-Knowledge-/Error-
  // Payloads aus tool_result-Events, als Karten unter dem Antworttext gerendert.
  payloads?: SelfKnowledgePayload[];
  // AgentGraph HITL: ein gated Write-Turn endet mit pending Approvals statt Text.
  pendingApprovals?: ApprovalView[];
  runId?: string;
  approvalDecisions?: Record<string, ApprovalDecision>;
  approvalState?: "open" | "running" | "done";
}

export interface ConvoSummary {
  id: string;
  title: string;
  updatedAt: string;
}

export interface VoiceOption {
  id: TtsVoice;
  label: string;
  provider: "piper" | "eleven";
  lang?: string;
  available?: boolean;
  unavailableReason?: string | null;
  actionLabel?: string;
  fallback?: boolean;
}

export interface TtsTestStatus {
  state: "ready" | "running" | "success" | "failed";
  label: string;
  detail: string;
}

export function fallbackVoiceOptions(reason = "Voice-Status konnte nicht geladen werden; SIMO nutzt Piper lokal."): VoiceOption[] {
  return [
    { id: "piper", label: "Piper Thorsten (lokal, DE)", provider: "piper", lang: "de", available: true, actionLabel: "Test sprechen", fallback: true },
    { id: "brian", label: "Brian - britisch tief (ElevenLabs)", provider: "eleven", lang: "en", available: false, unavailableReason: reason, actionLabel: "ElevenLabs-Key pruefen", fallback: false },
    { id: "daniel", label: "Daniel - britisch klar (ElevenLabs)", provider: "eleven", lang: "en", available: false, unavailableReason: reason, actionLabel: "ElevenLabs-Key pruefen", fallback: false },
    { id: "adam", label: "Adam - US warm (ElevenLabs)", provider: "eleven", lang: "en", available: false, unavailableReason: reason, actionLabel: "ElevenLabs-Key pruefen", fallback: false },
  ];
}

function simoChatErrorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

type SimoChatErrorContext = "stream" | "approval-approve" | "approval-reject" | "delete";

const SIMO_CHAT_ERROR_CONTEXT: Record<SimoChatErrorContext, { prefix: string; endpoint: string }> = {
  stream: { prefix: "Chat-Stream konnte nicht gestartet werden.", endpoint: "/api/chat/stream" },
  "approval-approve": { prefix: "Owner-Gate konnte nicht freigegeben werden.", endpoint: "/api/approval-inbox/:id/approve" },
  "approval-reject": { prefix: "Owner-Gate konnte nicht abgelehnt werden.", endpoint: "/api/approval-inbox/:id/reject" },
  delete: { prefix: "Konversation konnte nicht geloescht werden.", endpoint: "/api/conversations/:id" },
};

function simoChatErrorEvidence(copy: string, context: SimoChatErrorContext): string {
  const info = SIMO_CHAT_ERROR_CONTEXT[context];
  let text = copy
    .replace(/\s*Authorization(?:[:\s]+Bearer)?(?:[:\s]+[A-Za-z0-9._~+/=-]+)*/gi, "")
    .replace(/\s*Bearer\s+[A-Za-z0-9._~+/=-]+/gi, "")
    .replace(/\s*stack trace\b.*$/i, "")
    .replace(
      /\s*Endpoint im Fehler nicht enthalten; bitte Session erneuern oder Live-Daten neu laden\./,
      ". Bitte Session erneuern oder Live-Daten neu laden.",
    )
    .trim();
  if (!text.startsWith(info.prefix)) text = `${info.prefix} ${text}`;
  if (!/Endpoint:/i.test(text)) text = `${text} Endpoint: ${info.endpoint}.`;
  return text;
}

export function simoChatApprovalDecisionEndpoint(decision: ApprovalDecision): "/api/approval-inbox/:id/approve" | "/api/approval-inbox/:id/reject" {
  return decision === "approved" ? "/api/approval-inbox/:id/approve" : "/api/approval-inbox/:id/reject";
}

export async function simoChatDecideApprovalOnServer(
  approvalId: string,
  decision: ApprovalDecision,
): Promise<ApprovalInboxDecisionView> {
  const result = decision === "approved"
    ? await api.approvalInboxApprove(approvalId)
    : await api.approvalInboxReject(approvalId);
  if (!result.ok || result.approval.status !== decision) {
    throw new Error(`approval_decision_not_confirmed:${decision}`);
  }
  return result;
}

export function simoChatErrorCopy(error: unknown, context: SimoChatErrorContext | "approval" = "stream"): string {
  const resolvedContext: SimoChatErrorContext =
    context === "approval" ? "approval-approve" : context;
  const text = simoChatErrorText(error);
  if (/approval.*not found|already decided|not awaiting|invalid_state|approval_invalid|approval_expired/i.test(text)) {
    return simoChatErrorEvidence(
      "Owner-Gate ist nicht mehr aktuell. Bitte Chat-Status neu laden und die Freigabe erneut pruefen.",
      resolvedContext,
    );
  }
  if (/approval_decision_not_confirmed/i.test(text)) {
    return simoChatErrorEvidence(
      "Owner-Gate-Entscheidung wurde vom Server nicht bestaetigt. Bitte Approval Inbox neu laden und erneut pruefen.",
      resolvedContext,
    );
  }
  if (/conversation.*not found|conversation.*missing|delete.*not found|not owner of conversation|conversation_forbidden/i.test(text)) {
    return simoChatErrorEvidence(
      "Konversation ist nicht mehr aktuell. Bitte Verlauf neu laden und Aktion erneut pruefen.",
      resolvedContext,
    );
  }
  const safe = ownerFacingGlobalError(text);
  return simoChatErrorEvidence(safe, resolvedContext);
}

export function playableTtsVoice(input: {
  ttsVoice: TtsVoice;
  voiceOptions: VoiceOption[];
  elevenConfigured: boolean;
}): TtsVoice {
  const selected = input.voiceOptions.find((option) => option.id === input.ttsVoice);
  if (!selected || selected.available === false) return "piper";
  if (selected.provider === "eleven" && !input.elevenConfigured) return "piper";
  return selected.id;
}

export function resolveInitialTtsVoice(stored: string | null): TtsVoice {
  return stored === "piper" || stored === "brian" || stored === "daniel" || stored === "adam"
    ? stored
    : "brian";
}

// Phrasen für die "Suggest+Go"-Bestätigung per Voice
const CONFIRM_WORDS = ["ja", "go", "mach", "öffne", "öffnen", "okay", "ok"];
const CANCEL_WORDS = ["nein", "stopp", "stop", "abbrechen", "cancel"];

function matchVoiceCmd(text: string): "confirm" | "cancel" | null {
  const t = text.trim().toLowerCase().replace(/[.,!?]+$/g, "");
  if (CONFIRM_WORDS.some((w) => t === w || t.startsWith(w + " "))) return "confirm";
  if (CANCEL_WORDS.some((w) => t === w || t.startsWith(w + " "))) return "cancel";
  return null;
}

// Kompakte, bereits server-redigierte Args-Vorschau einer Approval.
export function approvalInputPreview(payloadPreviewJson: string): string | null {
  try {
    const p = JSON.parse(payloadPreviewJson) as { input?: unknown };
    if (p.input == null) return null;
    const s = JSON.stringify(p.input, null, 2);
    return s.length > 600 ? `${s.slice(0, 600)}…` : s;
  } catch {
    return null;
  }
}

const VIEW_KEY = "jarvis.chat.viewMode";
const VOICE_KEY = "jarvis.chat.voiceMode";
const TTS_VOICE_KEY = "jarvis.chat.ttsVoice";
const CONVO_KEY = "jarvis.chat.activeConversation";
const ROLE_KEY = "jarvis.chat.roleId";
const WAKEWORD_ENGINE_AVAILABLE = false;

export const TOOL_LABELS: Record<string, string> = {
  web_search: "sucht im Web",
  list_briefing: "liest dein Briefing",
  connector_status: "prüft Verbindungen",
  fetch_url: "lädt Webseite",
  draft_outlook_mail: "schreibt Outlook-Draft",
  draft_gmail: "schreibt Gmail-Draft",
  create_outlook_event: "legt Outlook-Termin an",
  create_gcal_event: "legt GCal-Termin an",
  add_github_comment: "kommentiert auf GitHub",
  add_itsimo_ticket_comment: "kommentiert IT-Simo-Ticket",
  memory_save: "merkt sich was",
  memory_recall: "schaut im Memory nach",
};

export const MODEL_OPTIONS = [
  { id: "", label: "Automatisch — SIMO wählt passend (Default)" },
  { id: "gpt-oss:20b", label: "gpt-oss:20b — schnell & klug" },
  { id: "qwen3:32b", label: "qwen3:32b — am klügsten, langsam" },
  { id: "qwen2.5:32b-instruct", label: "qwen2.5:32b-instruct — klug, mittel" },
  { id: "qwen2.5-coder:14b", label: "qwen2.5-coder:14b — schnell, Code" },
  { id: "llama3.1:8b", label: "llama3.1:8b — sehr schnell" },
];

export function modelOptionsForAssistant(assistantName: string): typeof MODEL_OPTIONS {
  return MODEL_OPTIONS.map((option) =>
    option.id === ""
      ? { ...option, label: `Automatisch — ${assistantName} wählt passend (Default)` }
      : option,
  );
}

/**
 * Zentrale SIMO-Chat-/Voice-/Model-/Approval-/Stream-Engine. Die gesamte Logik
 * der ehemaligen ChatView lebt hier, damit sie sowohl von der klassischen
 * ChatView als auch vom DashboardView konsumiert werden kann — ohne Duplikat
 * und ohne Verhaltensänderung (SIMO-HOLOGRAPHIC-DASHBOARD-SHELL-1).
 */
export function useSimoChat(options: { assistantName?: string } = {}) {
  const assistantName = options.assistantName ?? "SIMO";
  const [orb, setOrb] = useState<OrbState>("idle");
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<UiMessage[]>([]);
  const [convos, setConvos] = useState<ConvoSummary[]>([]);
  const [activeConvoId, setActiveConvoId] = useState<string | null>(() => localStorage.getItem(CONVO_KEY));
  const [confirmDeleteConvoId, setConfirmDeleteConvoId] = useState<string | null>(null);
  const [busyDeleteConvo, setBusyDeleteConvo] = useState(false);
  const [mode, setMode] = useState<ViewMode>(() =>
    (localStorage.getItem(VIEW_KEY) === "compact" ? "compact" : "orb"),
  );
  const [voiceMode, setVoiceMode] = useState<"off" | "server">(() =>
    (localStorage.getItem(VOICE_KEY) === "off" ? "off" : "server"),
  );
  const [model, setModel] = useState<string>(() => {
    // Einmalige Default-Switch-Migration VOR dem Lesen: Alt-Default gpt-oss:20b
    // → "" (Automatisch). Bewusste Wahlen bleiben (siehe modelMigration.ts).
    runModelDefaultMigration(localStorage);
    return localStorage.getItem(MODEL_KEY) ?? MODEL_OPTIONS[0]!.id;
  });
  const [ttsVoice, setTtsVoice] = useState<TtsVoice>(() => {
    return resolveInitialTtsVoice(localStorage.getItem(TTS_VOICE_KEY));
  });
  const [wakewordRequested, setWakewordRequested] = useState(() =>
    parseWakewordRequest(localStorage.getItem(WAKEWORD_REQUEST_KEY)),
  );
  const [roles, setRoles] = useState<AgentRole[]>([]);
  const [roleId, setRoleId] = useState<string>(() => localStorage.getItem(ROLE_KEY) ?? "");
  const [voiceOptions, setVoiceOptions] = useState<VoiceOption[]>(() => fallbackVoiceOptions());
  const [elevenConfigured, setElevenConfigured] = useState(false);
  const [ttsTestStatus, setTtsTestStatus] = useState<TtsTestStatus>({
    state: "ready",
    label: "Stimmtest bereit",
    detail: "Waehle eine Stimme und starte den Test.",
  });
  const sessionTokenPresent = hasSessionToken();

  const voice = useServerVoice();
  const wakewordBuildGateEnabled = parseWakewordBuildGate(import.meta.env as unknown as Record<string, unknown>);
  const wakewordGate = resolveWakewordGate({
    buildGateEnabled: wakewordBuildGateEnabled,
    requested: wakewordRequested,
    voiceMode,
    permission: voice.permission,
    engineAvailable: WAKEWORD_ENGINE_AVAILABLE,
  });
  const selectedMic = voice.devices.find((device) => device.deviceId === voice.selectedDeviceId);
  const canSelectMic = voice.permission === "granted";
  const voiceInputStatus = describeVoiceInputState({
    available: voice.available,
    voiceMode,
    permission: voice.permission,
    recording: voice.recording,
    processing: voice.processing,
    selectedDeviceLabel: selectedMic?.label ?? "",
    lastTrace: voice.lastTrace,
  });
  const activeTtsVoiceLabel = voiceOptions.find((option) => option.id === ttsVoice)?.label ?? ttsVoice;
  const voiceOwnerUx = deriveWebVoiceOwnerUxPolish({
    voiceMode,
    voiceAvailable: voice.available,
    permission: voice.permission,
    recording: voice.recording,
    processing: voice.processing,
    speaking: voice.speaking,
    selectedDeviceLabel: selectedMic?.label ?? "",
    ttsVoiceLabel: activeTtsVoiceLabel,
    wakewordGate,
    lastTrace: voice.lastTrace,
  });
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeStreamAbortRef = useRef<AbortController | null>(null);
  const activeConfirmAbortRef = useRef<AbortController | null>(null);
  // HITL-Approval-Absicherung (F-1): autoritativer Entscheidungsstand pro
  // Message-Bubble, plus In-Flight-/Resume-Guards.
  const approvalDecisionsRef = useRef<Record<string, Record<string, ApprovalDecision>>>({});
  const inFlightApprovalsRef = useRef<Set<string>>(new Set());
  const resumedRunsRef = useRef<Set<string>>(new Set());
  // Panel-Pfad (Command-Center ApprovalsPanel): Entscheidungen + Run-Snapshot
  // pro runId. Teilt sich inFlight-/Resume-Guards mit dem Transcript-Pfad.
  const panelRunSnapshotRef = useRef<Record<string, string[]>>({});
  const panelRunDecisionsRef = useRef<Record<string, Record<string, ApprovalDecision>>>({});
  // Cross-Convo-Resume: ein Approve aus fremder Konversation merkt sich hier den
  // gewünschten Resume, schaltet activeConvoId um und lässt den History-Load-
  // Effekt NACH dem Laden der Ziel-History resumen (race-sicher, kein Clobber).
  const resumeAfterLoadRef = useRef<{ runId: string; convoId: string } | null>(null);
  // Stabile Refs für Werte, die im globalen Keydown-Listener gelesen werden.
  const inputRef = useRef(input);
  const voiceModeRef = useRef(voiceMode);
  useEffect(() => {
    inputRef.current = input;
  }, [input]);
  useEffect(() => {
    voiceModeRef.current = voiceMode;
  }, [voiceMode]);

  useEffect(() => localStorage.setItem(VIEW_KEY, mode), [mode]);
  useEffect(() => localStorage.setItem(VOICE_KEY, voiceMode), [voiceMode]);
  useEffect(() => localStorage.setItem(MODEL_KEY, model), [model]);
  useEffect(() => localStorage.setItem(TTS_VOICE_KEY, ttsVoice), [ttsVoice]);
  useEffect(() => localStorage.setItem(ROLE_KEY, roleId), [roleId]);
  useEffect(() => {
    localStorage.setItem(WAKEWORD_REQUEST_KEY, serializeWakewordRequest(wakewordRequested));
  }, [wakewordRequested]);

  useEffect(() => {
    if (!sessionTokenPresent) {
      setRoles([]);
      setRoleId("");
      return;
    }
    let cancelled = false;
    void api
      .listRoles()
      .then((r) => {
        if (cancelled) return;
        const enabled = r.roles.filter((role) => role.enabled);
        setRoles(enabled);
        if (roleId && !enabled.some((role) => role.id === roleId)) setRoleId("");
      })
      .catch(() => {
        /* Rollen sind Komfort — Chat bleibt ohne Rolle nutzbar. */
      });
    return () => {
      cancelled = true;
    };
  }, [sessionTokenPresent]);

  // Verfügbare Stimmen vom Backend laden (zeigt auch ob ElevenLabs konfiguriert ist)
  useEffect(() => {
    if (!sessionTokenPresent) {
      setVoiceOptions(fallbackVoiceOptions("Owner-Session fehlt: ElevenLabs-Key-Status kann noch nicht geprueft werden."));
      setElevenConfigured(false);
      return;
    }
    let cancelled = false;
    void api
      .listVoices()
      .then((r) => {
        if (cancelled) return;
        setVoiceOptions(r.voices);
        setElevenConfigured(r.elevenConfigured);
        const selected = r.voices.find((v) => v.id === ttsVoice);
        if (!selected) {
          setTtsVoice("piper");
        }
      })
      .catch(() => {
        setVoiceOptions(fallbackVoiceOptions("Voice-Status konnte nicht geladen werden; SIMO nutzt Piper lokal bis zum naechsten Check."));
        setElevenConfigured(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionTokenPresent, ttsVoice]);
  useEffect(() => {
    if (activeConvoId) localStorage.setItem(CONVO_KEY, activeConvoId);
    else localStorage.removeItem(CONVO_KEY);
  }, [activeConvoId]);

  const loadConvos = (): void => {
    if (!sessionTokenPresent) {
      setConvos([]);
      return;
    }
    void api
      .listConversations()
      .then((r) => setConvos(r.items))
      .catch(() => {
        setConvos([]);
      });
  };
  useEffect(loadConvos, [sessionTokenPresent]);

  useEffect(() => {
    if (!sessionTokenPresent || !activeConvoId) {
      setHistory([]);
      return;
    }
    void api
      .getConversation(activeConvoId)
      .then((c) => {
        setHistory(
          c.messages.map((m) => ({
            id: String(m.id),
            role: m.role,
            content: m.content,
            meta: m.iterations != null ? { iterations: m.iterations, toolCalls: 0, model: m.model } : undefined,
          })),
        );
        // Cross-Convo-Resume: wurde dieser Wechsel durch ein Approve eines
        // fremden Runs ausgelöst, jetzt — nach dem Laden der Ziel-History —
        // resumen. So hängt die Pending-Bubble an der Ziel-Convo (kein Clobber).
        const pendingResume = resumeAfterLoadRef.current;
        if (pendingResume && pendingResume.convoId === activeConvoId) {
          resumeAfterLoadRef.current = null;
          if (!resumedRunsRef.current.has(pendingResume.runId)) {
            resumedRunsRef.current.add(pendingResume.runId);
            void resumeRun(pendingResume.runId);
          }
        }
      })
      .catch(() => {
        // Ziel-Convo-Load fehlgeschlagen: Resume-Wunsch verwerfen, Run NICHT
        // permanent als resumed sperren → erneuter Versuch bleibt möglich.
        if (resumeAfterLoadRef.current?.convoId === activeConvoId) resumeAfterLoadRef.current = null;
        setActiveConvoId(null);
      });
  }, [activeConvoId, sessionTokenPresent]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [history]);

  useEffect(() => {
    if (voice.recording) setOrb("wake");
    else if (voice.speaking && orb !== "speaking") setOrb("speaking");
    else if (!voice.speaking && orb === "speaking" && voiceMode === "server") setOrb("idle");
    else if (orb === "wake" && !voice.recording) setOrb("idle");
  }, [voice.recording, voice.speaking, voiceMode]);

  const stopAll = (): void => {
    activeStreamAbortRef.current?.abort();
    activeStreamAbortRef.current = null;
    activeConfirmAbortRef.current?.abort();
    activeConfirmAbortRef.current = null;
    voice.cancelSpeech();
    voice.cancelRecording();
    setHistory((h) =>
      h.map((m) =>
        m.pending
          ? {
              ...m,
              pending: false,
              content: m.content || "Anfrage gestoppt.",
            }
          : m,
      ),
    );
    setOrb("idle");
  };

  const testTtsVoice = async (): Promise<void> => {
    const safeVoice = playableTtsVoice({ ttsVoice, voiceOptions, elevenConfigured });
    const lang = safeVoice === "piper" ? "de" : "en";
    const text = safeVoice === "piper"
      ? "SIMO Stimme ist bereit."
      : "SIMO voice is ready.";
    const selected = voiceOptions.find((option) => option.id === ttsVoice);
    const selectedLabel = selected?.label ?? ttsVoice;
    const playbackLabel = safeVoice === "piper"
      ? "Piper lokal"
      : voiceOptions.find((option) => option.id === safeVoice)?.label ?? safeVoice;
    setTtsTestStatus({
      state: "running",
      label: "Stimmtest laeuft",
      detail: `${selectedLabel} gewaehlt; Test spielt ${playbackLabel}.`,
    });
    const result = await voice.speak(text, lang, safeVoice);
    if (result.ok) {
      setTtsTestStatus({
        state: "success",
        label: "Stimmtest erfolgreich",
        detail: `${playbackLabel} wurde abgespielt.`,
      });
      return;
    }
    setTtsTestStatus({
      state: "failed",
      label: "Stimmtest fehlgeschlagen",
      detail: ownerFacingGlobalError(result.error ?? "TTS: unbekannter Fehler"),
    });
  };

  useEffect(() => {
    const onStop = (): void => stopAll();
    window.addEventListener("jarvis-stop", onStop);
    return () => window.removeEventListener("jarvis-stop", onStop);
  }, []);

  // Konsumiert den SSE-Event-Stream in die Assistant-Bubble `asstTmpId`.
  const processStream = async (
    stream: AsyncIterable<{ event: string; data: Record<string, unknown> }>,
    asstTmpId: string,
  ): Promise<{
    finalText: string; toolCalls: number; iterations: number;
    resultConvoId: string | null; assistantMode: string | null;
    pendingDraft: { webLink: string; tool: string } | null; speechText: string;
    debugMeta: AssistantDebugView | null; pendingApprovalIds: string[]; runId: string | null;
    payloads: SelfKnowledgePayload[];
  }> => {
    let liveText = "";
    let toolCalls = 0;
    let iterations = 0;
    let resultConvoId: string | null = null;
    let assistantMode: string | null = null;
    let pendingDraft: { webLink: string; tool: string } | null = null;
    let speechText = "";
    let debugMeta: AssistantDebugView | null = null;
    let runId: string | null = null;
    let pendingApprovalIds: string[] = [];
    const toolLog: string[] = [];
    const payloads: SelfKnowledgePayload[] = [];
    const updateBubble = (): void => {
      setHistory((h) =>
        h.map((m) =>
          m.id === asstTmpId
            ? {
                ...m,
                content: liveText || (toolLog.length > 0 ? `… ${toolLog.join(", ")}` : ""),
                pending: liveText === "",
                payloads: payloads.length > 0 ? [...payloads] : undefined,
              }
            : m,
        ),
      );
    };
    for await (const ev of stream) {
      if (ev.event === "status") {
        if (ev.data.state === "acting") setOrb("acting");
        else if (ev.data.state === "thinking") setOrb("thinking");
      } else if (ev.event === "text_chunk") {
        liveText += String(ev.data.text ?? "");
        updateBubble();
        if (orb !== "speaking") setOrb("speaking");
      } else if (ev.event === "tool_call") {
        toolCalls++;
        const name = String(ev.data.name ?? "tool");
        toolLog.push(TOOL_LABELS[name] ?? name);
        updateBubble();
      } else if (ev.event === "tool_result") {
        const link = String(ev.data.webLink ?? "");
        if (link) pendingDraft = { webLink: link, tool: String(ev.data.name ?? "") };
        if (ev.data.payload) {
          const detected = detectSelfKnowledgePayload(ev.data.payload);
          if (detected) {
            payloads.push(detected);
            updateBubble();
          }
        }
      } else if (ev.event === "meta") {
        resultConvoId = String(ev.data.conversationId ?? "");
        assistantMode = String(ev.data.assistantMode ?? "");
        if (ev.data.runId) runId = String(ev.data.runId);
      } else if (ev.event === "debug") {
        debugMeta = ev.data as unknown as AssistantDebugView;
        speechText = String((ev.data as Record<string, unknown>).speechText ?? "");
      } else if (ev.event === "done") {
        iterations = Number(ev.data.iterations ?? 0);
        if (ev.data.toolCalls != null) toolCalls = Number(ev.data.toolCalls);
        if (Array.isArray(ev.data.pendingApprovals)) {
          pendingApprovalIds = (ev.data.pendingApprovals as unknown[]).map((x) => String(x));
        }
      } else if (ev.event === "error") {
        throw new Error(String(ev.data.error ?? "Stream-Fehler"));
      }
    }
    return { finalText: liveText, toolCalls, iterations, resultConvoId, assistantMode, pendingDraft, speechText, debugMeta, pendingApprovalIds, runId, payloads };
  };

  const finalizeBubble = (asstTmpId: string, out: Awaited<ReturnType<typeof processStream>>): void => {
    setHistory((h) =>
      h.map((m) =>
        m.id === asstTmpId
          ? {
              ...m,
              content: out.finalText,
              pending: false,
              meta: {
                iterations: out.iterations,
                toolCalls: out.toolCalls,
                model,
                assistantMode: out.debugMeta?.assistantMode ?? out.assistantMode,
                sanityStatus: out.debugMeta?.sanityStatus ?? null,
                activeToolGroups: out.debugMeta?.activeToolGroups ?? [],
                blockedToolGroups: out.debugMeta?.blockedToolGroups ?? [],
                usedToolNames: out.debugMeta?.usedToolNames ?? [],
                latencies: out.debugMeta?.latencies ?? null,
              },
              draft: out.pendingDraft ?? undefined,
              payloads: out.payloads.length > 0 ? out.payloads : undefined,
            }
          : m,
      ),
    );
  };

  const sendText = async (text: string): Promise<void> => {
    if (!text.trim()) return;
    stopAll();
    setError(null);
    setInput("");
    const userTmpId = `u-${Date.now()}`;
    const asstTmpId = `a-${Date.now()}`;
    const controller = new AbortController();
    activeStreamAbortRef.current = controller;
    setHistory((h) => [
      ...h,
      { id: userTmpId, role: "user", content: text },
      { id: asstTmpId, role: "assistant", content: "", pending: true },
    ]);
    setOrb("thinking");

    try {
      const stream = api.chatStream(
        { message: text, conversationId: activeConvoId ?? undefined, model: model || undefined, roleId: roleId || undefined },
        controller.signal,
      );
      const out = await processStream(stream, asstTmpId);

      if (out.pendingApprovalIds.length > 0) {
        let cards: ApprovalView[] = [];
        try {
          const all = await api.listApprovals();
          cards = all.approvals.filter((a) => out.pendingApprovalIds.includes(a.id));
        } catch {
          /* Details optional — Buttons brauchen nur die IDs */
        }
        const resumeId = out.runId ?? cards[0]?.runId ?? null;
        setHistory((h) =>
          h.map((m) =>
            m.id === asstTmpId
              ? {
                  ...m,
                  pending: false,
                  content: out.finalText || `${assistantName} möchte eine Aktion ausführen und braucht deine Freigabe:`,
                  pendingApprovals: cards,
                  runId: resumeId ?? undefined,
                  approvalDecisions: {},
                  approvalState: "open",
                }
              : m,
          ),
        );
        loadConvos();
        if (voiceMode === "server" && !controller.signal.aborted) {
          try {
            const safeVoice = playableTtsVoice({ ttsVoice, voiceOptions, elevenConfigured });
            await voice.speak("Ich brauche deine Freigabe für diese Aktion.", "de", safeVoice);
          } catch {
            /* Speak-Fehler ignorieren — Karte bleibt sichtbar */
          }
        }
        setOrb("idle");
        return;
      }

      finalizeBubble(asstTmpId, out);
      if (out.resultConvoId && out.resultConvoId !== activeConvoId) setActiveConvoId(out.resultConvoId);
      loadConvos();

      if (voiceMode === "server" && out.finalText) {
        const speakBase = out.speechText || out.finalText;
        const speakText = out.pendingDraft ? `${speakBase} Soll ich den Entwurf öffnen?` : speakBase;
        const ttsDecision = prepareOwnerTtsText(speakText);
        // TTS ist Komfort — ein Speak-Fehler darf die bereits finalisierte
        // Antwort NICHT in den äußeren catch (Bubble-Löschung) reißen.
        try {
          if (ttsDecision.shouldSpeak) {
            const safeVoice = playableTtsVoice({ ttsVoice, voiceOptions, elevenConfigured });
            await voice.speak(ttsDecision.text, "de", safeVoice);
          }
        } catch {
          /* Speak-Fehler ignorieren — die Antwort steht bereits im Chat */
        }
        if (out.pendingDraft && !controller.signal.aborted && voiceModeRef.current === "server") {
          setOrb("confirming");
          void waitForConfirmation(out.pendingDraft);
        } else if (!controller.signal.aborted) {
          setOrb("idle");
        }
      } else {
        setOrb("speaking");
        setTimeout(() => setOrb("idle"), 1500);
      }
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") {
        setOrb("idle");
        return;
      }
      setError(simoChatErrorCopy(e, "stream"));
      setOrb("error");
      setHistory((h) => h.filter((m) => m.id !== asstTmpId));
    } finally {
      activeStreamAbortRef.current = null;
    }
  };

  // Setzt einen freigegebenen Run fort: neue Assistant-Bubble streamt das Ergebnis.
  const resumeRun = async (runId: string): Promise<void> => {
    const asstTmpId = `a-${Date.now()}`;
    const controller = new AbortController();
    activeStreamAbortRef.current = controller;
    setHistory((h) => [...h, { id: asstTmpId, role: "assistant", content: "", pending: true }]);
    setOrb("acting");
    try {
      const stream = api.chatStream(
        { resumeRunId: runId, conversationId: activeConvoId ?? undefined, model: model || undefined, roleId: roleId || undefined },
        controller.signal,
      );
      const out = await processStream(stream, asstTmpId);
      finalizeBubble(asstTmpId, out);
      loadConvos();
      if (voiceMode === "server" && out.finalText && !controller.signal.aborted) {
        const ttsDecision = prepareOwnerTtsText(out.speechText || out.finalText);
        try {
          if (ttsDecision.shouldSpeak) {
            const safeVoice = playableTtsVoice({ ttsVoice, voiceOptions, elevenConfigured });
            await voice.speak(ttsDecision.text, "de", safeVoice);
          }
        } catch {
          /* Speak-Fehler ignorieren — die Antwort steht bereits im Chat */
        }
      }
      setOrb("idle");
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") {
        setOrb("idle");
        return;
      }
      setError(simoChatErrorCopy(e, "stream"));
      setOrb("error");
      setHistory((h) => h.filter((m) => m.id !== asstTmpId));
    } finally {
      activeStreamAbortRef.current = null;
    }
  };

  // Approve/Reject einer einzelnen Approval (mit Race-/Doppelklick-Schutz).
  const decideApproval = async (
    msgId: string,
    approval: ApprovalView,
    decision: ApprovalDecision,
  ): Promise<void> => {
    setError(null);
    const msg = history.find((m) => m.id === msgId);
    if (!msg) return;
    if (inFlightApprovalsRef.current.has(approval.id)) return;
    const committed = approvalDecisionsRef.current[msgId] ?? {};
    if (committed[approval.id]) return;
    inFlightApprovalsRef.current.add(approval.id);

    setHistory((h) =>
      h.map((m) =>
        m.id === msgId
          ? { ...m, approvalDecisions: mergeDecision(m.approvalDecisions ?? {}, approval.id, decision) }
          : m,
      ),
    );

    try {
      await simoChatDecideApprovalOnServer(approval.id, decision);
    } catch (e) {
      inFlightApprovalsRef.current.delete(approval.id);
      setHistory((h) =>
        h.map((m) => {
          if (m.id !== msgId) return m;
          const d = { ...(m.approvalDecisions ?? {}) };
          delete d[approval.id];
          return { ...m, approvalDecisions: d };
        }),
      );
      setError(simoChatErrorCopy(e, decision === "approved" ? "approval-approve" : "approval-reject"));
      return;
    }

    const nextDecisions = mergeDecision(approvalDecisionsRef.current[msgId] ?? {}, approval.id, decision);
    approvalDecisionsRef.current[msgId] = nextDecisions;

    if (
      allApprovalsDecided(msg.pendingApprovals ?? [], nextDecisions) &&
      msg.runId &&
      !resumedRunsRef.current.has(msg.runId)
    ) {
      resumedRunsRef.current.add(msg.runId);
      setHistory((h) => h.map((m) => (m.id === msgId ? { ...m, approvalState: "running" } : m)));
      await resumeRun(msg.runId);
      setHistory((h) => h.map((m) => (m.id === msgId ? { ...m, approvalState: "done" } : m)));
    }
  };

  // Ruft den Decide-Endpoint und meldet, ob der Server die Entscheidung
  // bestätigt hat. Setzt bei Fehler/404/stale eine Fehlermeldung. Kein
  // optimistisches UI-Update hier — der Aufrufer committet erst bei ok.
  const runApprovalApi = async (
    approval: ApprovalView,
    decision: ApprovalDecision,
  ): Promise<boolean> => {
    try {
      await simoChatDecideApprovalOnServer(approval.id, decision);
      return true;
    } catch (e) {
      setError(simoChatErrorCopy(e, decision === "approved" ? "approval-approve" : "approval-reject"));
      return false;
    }
  };

  // Panel-seitige Entscheidung, gekeyt per runId (Approval hängt nicht
  // zwingend an einer Chat-Bubble). Teilt inFlight-/Resume-Guards mit dem
  // Transcript-Pfad. Gibt { ok } zurück, damit das Panel nur bei Erfolg als
  // entschieden behandelt (Schärfung 1: kein stiller Erfolg).
  //
  // Single-Batch pro runId ist per Code bewiesen (buildResumeGraph in
  // runtime.ts:166-177 hat keinen approval-Node) → Snapshot/resumedRunsRef pro
  // runId einmalig korrekt (Spec Decision-Log). Pre-Review P2: Entscheidungs-Refs
  // werden bidirektional mit der passenden Transcript-Bubble abgeglichen.
  const decideApprovalByRun = async (
    approval: ApprovalView,
    runApprovals: ApprovalView[],
    decision: ApprovalDecision,
  ): Promise<{ ok: boolean }> => {
    setError(null);
    if (inFlightApprovalsRef.current.has(approval.id)) return { ok: false };
    // Snapshot der Run-Approvals beim ersten Touch — stabil gegen das
    // Live-Schrumpfen der Pending-Liste (spiegelt die msgId-Snapshot-Semantik
    // des Transcripts). Beim ersten Touch zusätzlich Decisions aus der passenden
    // Transcript-Bubble vorbefüllen (Transcript→Panel, P2).
    if (!panelRunSnapshotRef.current[approval.runId]) {
      panelRunSnapshotRef.current[approval.runId] = runApprovals.map((a) => a.id);
      const seedBubble = history.find((m) => m.runId === approval.runId);
      if (seedBubble) {
        panelRunDecisionsRef.current[approval.runId] = {
          ...(panelRunDecisionsRef.current[approval.runId] ?? {}),
          ...(approvalDecisionsRef.current[seedBubble.id] ?? {}),
        };
      }
    }
    const already = panelRunDecisionsRef.current[approval.runId] ?? {};
    if (already[approval.id]) return { ok: false };

    inFlightApprovalsRef.current.add(approval.id);
    const ok = await runApprovalApi(approval, decision);
    inFlightApprovalsRef.current.delete(approval.id);
    if (!ok) {
      // Kein Commit, kein Resume. Panel lädt den echten Server-Stand neu.
      return { ok: false };
    }

    const nextDecisions = mergeDecision(
      panelRunDecisionsRef.current[approval.runId] ?? {},
      approval.id,
      decision,
    );
    panelRunDecisionsRef.current[approval.runId] = nextDecisions;

    // Falls dieselbe Approval auch an einer Transcript-Bubble hängt: dort als
    // entschieden markieren — Anzeige-State (kein stale Doppelklick) UND der
    // autoritative approvalDecisionsRef (Panel→Transcript, P2), damit der
    // Transcript-Resume-Check die Panel-Entscheidung mitzählt.
    const bubble = history.find((m) => (m.pendingApprovals ?? []).some((a) => a.id === approval.id));
    if (bubble) {
      approvalDecisionsRef.current[bubble.id] = mergeDecision(
        approvalDecisionsRef.current[bubble.id] ?? {},
        approval.id,
        decision,
      );
      setHistory((h) =>
        h.map((m) =>
          m.id === bubble.id
            ? { ...m, approvalDecisions: mergeDecision(m.approvalDecisions ?? {}, approval.id, decision) }
            : m,
        ),
      );
    }

    // Resume genau einmal, sobald alle Approvals des Run-Snapshots entschieden
    // sind. resumedRunsRef ist mit dem Transcript-Pfad geteilt → kein
    // Doppel-Resume, egal welche Fläche zuletzt entschieden hat.
    const snapshotPending = (panelRunSnapshotRef.current[approval.runId] ?? []).map((id) => ({ id }));
    if (allApprovalsDecided(snapshotPending, nextDecisions) && !resumedRunsRef.current.has(approval.runId)) {
      if (needsConvoSwitch(approval.conversationId, activeConvoId)) {
        // Fremde Konversation: erst dorthin wechseln; der History-Load-Effekt
        // löst den Resume danach aus (resumedRunsRef wird DORT gesetzt, damit ein
        // fehlgeschlagener Load den Run nicht permanent als resumed sperrt).
        resumeAfterLoadRef.current = { runId: approval.runId, convoId: approval.conversationId };
        setActiveConvoId(approval.conversationId);
      } else {
        resumedRunsRef.current.add(approval.runId);
        await resumeRun(approval.runId);
      }
    }
    return { ok: true };
  };

  /**
   * Lauscht bis zu 60 s auf eine kurze Bestätigung per Voice.
   */
  const waitForConfirmation = async (draft: { webLink: string; tool: string }): Promise<void> => {
    const deadline = Date.now() + 60_000;
    const controller = new AbortController();
    activeConfirmAbortRef.current = controller;
    while (Date.now() < deadline) {
      if (controller.signal.aborted) {
        activeConfirmAbortRef.current = null;
        setOrb("idle");
        return;
      }
      try {
        await voice.startRecording();
      } catch {
        activeConfirmAbortRef.current = null;
        setOrb("idle");
        return;
      }
      await new Promise<void>((resolve) => {
        const t = setTimeout(resolve, 5000);
        controller.signal.addEventListener(
          "abort",
          () => {
            clearTimeout(t);
            resolve();
          },
          { once: true },
        );
      });
      if (controller.signal.aborted) {
        voice.cancelRecording();
        activeConfirmAbortRef.current = null;
        setOrb("idle");
        return;
      }
      const text = await voice.stopRecording();
      if (!text) continue;
      const cmd = matchVoiceCmd(text);
      if (cmd === "confirm") {
        window.open(draft.webLink, "_blank", "noopener");
        const safeVoice = playableTtsVoice({ ttsVoice, voiceOptions, elevenConfigured });
        await voice.speak("Geöffnet.", "de", safeVoice);
        activeConfirmAbortRef.current = null;
        setOrb("idle");
        return;
      }
      if (cmd === "cancel") {
        const safeVoice = playableTtsVoice({ ttsVoice, voiceOptions, elevenConfigured });
        await voice.speak("Okay, verworfen.", "de", safeVoice);
        activeConfirmAbortRef.current = null;
        setOrb("idle");
        return;
      }
    }
    activeConfirmAbortRef.current = null;
    setOrb("idle");
  };

  const newConversation = async (): Promise<void> => {
    const c = await api.newConversation();
    setActiveConvoId(c.id);
    setHistory([]);
    loadConvos();
  };

  const deleteConvo = (id: string): void => {
    setConfirmDeleteConvoId(id);
  };

  const performDeleteConvo = async (): Promise<void> => {
    if (!confirmDeleteConvoId) return;
    setBusyDeleteConvo(true);
    try {
      const id = confirmDeleteConvoId;
      await api.deleteConversation(id);
      if (activeConvoId === id) {
        setActiveConvoId(null);
        setHistory([]);
      }
      loadConvos();
      setConfirmDeleteConvoId(null);
    } catch (e) {
      setError(simoChatErrorCopy(e, "delete"));
    } finally {
      setBusyDeleteConvo(false);
    }
  };

  // Stabile Handler-Refs damit der Keyboard-Effekt nur EINMAL attached
  const handleMicDownRef = useRef<() => Promise<void>>(async () => {});
  const handleMicUpRef = useRef<() => Promise<void>>(async () => {});
  handleMicDownRef.current = async (): Promise<void> => {
    if (voiceMode === "off") return;
    setError(null);
    activeConfirmAbortRef.current?.abort();
    activeConfirmAbortRef.current = null;
    voice.cancelSpeech();
    activeStreamAbortRef.current?.abort();
    try {
      await voice.startRecording();
    } catch (e) {
      setError(ownerFacingGlobalError(`Mikrofon-Zugriff: ${e instanceof Error ? e.message : e}`));
    }
  };
  handleMicUpRef.current = async (): Promise<void> => {
    setOrb("thinking");
    const text = await voice.stopRecording();
    if (text && text.trim()) {
      setInput(text);
      void sendText(text);
    } else {
      setOrb("idle");
    }
  };
  const handleMicDown = (): void => void handleMicDownRef.current();
  const handleMicUp = (): void => void handleMicUpRef.current();

  // Push-To-Talk-State haelt ueber Re-Renders.
  const isPttDownRef = useRef(false);
  useEffect(() => {
    const onDown = (e: KeyboardEvent): void => {
      if (e.code !== "Space" || e.repeat) return;
      const active = document.activeElement;
      const typingInInput = active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement;
      if (typingInInput && inputRef.current.trim().length > 0) return;
      if (voiceModeRef.current === "off") return;
      e.preventDefault();
      if (!isPttDownRef.current) {
        isPttDownRef.current = true;
        void handleMicDownRef.current();
      }
    };
    const onUp = (e: KeyboardEvent): void => {
      if (e.code !== "Space") return;
      if (voiceModeRef.current === "off") {
        if (isPttDownRef.current) {
          isPttDownRef.current = false;
          voice.cancelRecording();
        }
        return;
      }
      if (isPttDownRef.current) {
        isPttDownRef.current = false;
        void handleMicUpRef.current();
      }
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
    };
  }, []);

  return {
    orb, setOrb,
    input, setInput,
    error, setError,
    history,
    convos,
    activeConvoId, setActiveConvoId,
    confirmDeleteConvoId, setConfirmDeleteConvoId,
    busyDeleteConvo,
    mode, setMode,
    voiceMode, setVoiceMode,
    model, setModel,
    roleId, setRoleId, roles,
    ttsVoice, setTtsVoice,
    voiceOptions, elevenConfigured,
    wakewordGate, wakewordRequested, setWakewordRequested,
    voice,
    selectedMic, canSelectMic, voiceInputStatus,
    voiceOwnerUx,
    ttsTestStatus,
    scrollRef,
    sendText, stopAll, testTtsVoice, decideApproval, decideApprovalByRun,
    newConversation, deleteConvo, performDeleteConvo,
    handleMicDown, handleMicUp,
  };
}

export type SimoChat = ReturnType<typeof useSimoChat>;
